import { Typography } from '@/components/atoms';
import { useI18nContext } from '@/core/providers/I18nProvider';
import React, { useMemo } from 'react';
import { Platform, Pressable, StyleSheet, View } from 'react-native';
import { getGuestDetailsTypeScale } from '../constants/guestLayout';
import { useGuestLabels } from '../hooks/useGuestLabels';

export interface GuestFloatingLoginButtonProps {
  readonly onPress: () => void;
  readonly isPhone?: boolean;
  readonly isTablet?: boolean;
}

/** Figma Spacing scale (4px base): Spacing/3=12, Spacing/5=20, Spacing/8=32. */
const SPACING_3 = 12;
const SPACING_5 = 20;
const SPACING_8 = 32;
/** Figma: UI-Colours-Button-Default for this overlay. */
const OVERLAY_BUTTON_BG = '#1A1A1A';

/**
 * Floating Login CTA on guest listing and unit details.
 * Phone: full-width bottom bar.
 * Larger: hugs label; LTR → bottom-right, RTL → bottom-left.
 * Web uses `fixed` so it stays on the viewport.
 */
export function GuestFloatingLoginButton({
  onPress,
  isPhone = false,
  isTablet = false,
}: Readonly<GuestFloatingLoginButtonProps>) {
  const labels = useGuestLabels();
  const { isRTL } = useI18nContext();
  const typeScale = useMemo(
    () => getGuestDetailsTypeScale({ isPhone, isTablet }),
    [isPhone, isTablet],
  );

  let edgeStyle: typeof styles.wrapPhone | typeof styles.wrapBottomLeft | typeof styles.wrapBottomRight =
    styles.wrapBottomRight;
  if (isPhone) {
    edgeStyle = styles.wrapPhone;
  } else if (isRTL) {
    edgeStyle = styles.wrapBottomLeft;
  }

  return (
    <View
      style={[
        styles.wrap,
        Platform.OS === 'web' ? styles.wrapFixedWeb : null,
        edgeStyle,
      ]}
      pointerEvents="box-none"
    >
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={labels.login}
        onPress={onPress}
        style={({ pressed }) => [
          styles.button,
          isPhone && styles.buttonPhone,
          {
            height: typeScale.loginButtonHeight,
            opacity: pressed ? 0.92 : 1,
          },
          Platform.OS === 'web'
            ? styles.buttonShadowWeb
            : styles.buttonShadowNative,
        ]}
      >
        <Typography
          variant={typeScale.loginButtonLabel}
          color="white"
          numberOfLines={1}
          style={[
            styles.label,
            { lineHeight: typeScale.loginButtonLineHeight },
          ]}
        >
          {labels.login}
        </Typography>
      </Pressable>
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    position: 'absolute',
    bottom: 32,
    zIndex: 20,
  },
  /** Stay on the viewport bottom — parent absolute was landing mid-content. */
  wrapFixedWeb: {
    position: 'fixed' as unknown as 'absolute',
  },
  wrapBottomRight: {
    right: 24,
  },
  wrapBottomLeft: {
    left: 24,
  },
  wrapPhone: {
    left: 16,
    right: 16,
    bottom: 16,
    alignItems: 'center',
  },
  button: {
    /** Hug label — Arabic "تسجيل الدخول" needs more than Figma EN 184. */
    minWidth: 184,
    maxWidth: 320,
    paddingVertical: SPACING_3,
    paddingHorizontal: SPACING_5,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: SPACING_8,
    backgroundColor: OVERLAY_BUTTON_BG,
  },
  buttonPhone: {
    width: '100%',
    maxWidth: 270,
    minWidth: 0,
  },
  buttonShadowWeb: {
    // Figma: 0px 0px 24px 0px #0000009E
    boxShadow: '0px 0px 24px 0px #0000009E',
  },
  buttonShadowNative: {
    shadowColor: '#000000',
    shadowOpacity: 0.62,
    shadowRadius: 24,
    shadowOffset: { width: 0, height: 0 },
    elevation: 12,
  },
  label: {
    letterSpacing: 0,
    textAlign: 'center',
    flexShrink: 1,
  },
});
